// The `ui` slice survives a refresh via sessionStorage — but only for the link it came from.
//
// store_v2 was not tied to a token: opening a different (or dead) link in the same tab
// rehydrated the PREVIOUS applicant's userDetails and showed their name, email and phone. It
// also held the whole application, every co-applicant's contact details and link token
// included. store_v3 records which token the snapshot belongs to, and a snapshot for any other
// token (or none) is discarded on load.
//
// It records a fingerprint of the token, not the token: the token is the applicant's session
// credential and is deliberately kept out of storage (see applicantToken.ts). The fingerprint
// only has to tell two links apart; it is not a security boundary.
const STORAGE_KEY = 'store_v3';
const LEGACY_KEYS = ['store_v2'];

interface PersistedSnapshot {
  tokenHash: string | null;
  state: unknown;
}

/**
 * Pure: a short one-way fingerprint of the token (cyrb53, 53 bits). Link tokens are signed JWTs,
 * so this can't be turned back into one; a collision would at worst rehydrate another link's
 * cached UI state, which the applicant fetch then overwrites.
 */
export const tokenFingerprint = (token: string): string => {
  let h1 = 0xdeadbeef;
  let h2 = 0x41c6ce57;
  for (let i = 0; i < token.length; i++) {
    const ch = token.charCodeAt(i);
    h1 = Math.imul(h1 ^ ch, 2654435761);
    h2 = Math.imul(h2 ^ ch, 1597334677);
  }
  h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507);
  h1 ^= Math.imul(h2 ^ (h2 >>> 13), 3266489909);
  h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507);
  h2 ^= Math.imul(h1 ^ (h1 >>> 13), 3266489909);
  return (4294967296 * (2097151 & h2) + (h1 >>> 0)).toString(36);
};

/** Pure: the persisted state in `raw` if it belongs to `token`, else null. */
export const persistedStateFor = (raw: string | null, token: string | null): any => {
  if (!raw || !token) return null;
  try {
    const snapshot = JSON.parse(raw) as Partial<PersistedSnapshot> | null;
    return snapshot && snapshot.tokenHash === tokenFingerprint(token) ? snapshot.state ?? null : null;
  } catch {
    return null;
  }
};

export const loadPersistedState = (token: string | null): any => {
  try {
    LEGACY_KEYS.forEach(key => sessionStorage.removeItem(key));
    const state = persistedStateFor(sessionStorage.getItem(STORAGE_KEY), token);
    if (!state) sessionStorage.removeItem(STORAGE_KEY);
    return state;
  } catch {
    return null;
  }
};

export const savePersistedState = (token: string | null, state: unknown): void => {
  try {
    const snapshot: PersistedSnapshot = { tokenHash: token ? tokenFingerprint(token) : null, state };
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(snapshot));
  } catch {
    // Storage blocked or full: the app still works, a refresh just starts from the token again.
  }
};
